import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  AgentInfo,
  CommandInfo,
  Part,
  Provider,
  ProviderUsageInfo,
  Session,
  SessionMessage,
  SessionStatusInfo,
} from "../types";
import { Markdown } from "../markdown";
import { cx, formatCost, formatTokens, languageFromFilename } from "../util";
import { ModelSelect, type ModelChoice } from "./ModelSelect";
import { getProviderUsage, needsReserveSwitch, PROVIDER_USAGE_UPDATED, remainingPercent, resetIn, shortWindowLabel } from "../usage";

export interface Attachment {
  uri: string;
  name: string;
  size: number;
}

interface ChatViewProps {
  session: Session | null;
  messages: SessionMessage[];
  busy: boolean;
  status?: SessionStatusInfo;
  providers: Provider[];
  connected?: string[];
  agents: AgentInfo[];
  commands: CommandInfo[];
  model: ModelChoice | null;
  onModelChange: (m: ModelChoice | null) => void;
  agent: string;
  onAgentChange: (a: string) => void;
  allowAppRun: boolean;
  allowAllPermissions: boolean;
  permissionsSaving: boolean;
  sessionPrefsLoading: boolean;
  onAllowAllPermissionsChange: (allow: boolean) => void;
  onToggleAllowAppRun: () => void;
  onSend: (text: string, files: Attachment[]) => void | Promise<void>;
  onAbort: () => void;
  onUpload: (file: File) => Promise<Attachment>;
  onFork: (messageID?: string) => void;
  onSummarize: () => void;
  onShareToggle: () => void;
  onRevert: (messageID: string) => void;
  onUnrevert: () => void;
  onInit: () => void;
  onRunCommand: (command: string, args: string) => void;
  onOpenSettings: () => void;
}

function statusLabel(status?: SessionStatusInfo): string | null {
  if (!status || status.type === "idle") return null;
  return status.message ?? (status.type === "retry" ? "Nova tentativa em andamento…" : "Executando…");
}

// ── Renderização de partes de mensagem ─────────────────────────────

function ToolPartView({ part }: { part: Part }) {
  const [open, setOpen] = useState(part.state?.status === "error");
  const state = part.state;
  const status = state?.status ?? "pending";
  const icon = status === "completed" ? "✓" : status === "error" ? "✕" : status === "running" ? "⟳" : "·";
  const chipClass = status === "completed" ? "ok" : status === "error" ? "err" : "warn";

  return (
    <div className="part tool-card">
      <div
        className="tool-head"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") setOpen((v) => !v);
        }}
      >
        <span className={cx("chip", chipClass)} title={`Estado: ${status}`}>
          {icon} {status === "running" ? "executando" : status === "completed" ? "concluído" : status === "error" ? "erro" : "pendente"}
        </span>
        <span className="tool-name">{part.tool ?? "ferramenta"}</span>
        <span className="tool-title">{state?.title ?? ""}</span>
        <span aria-hidden style={{ color: "var(--text-faint)" }}>
          {open ? "▾" : "▸"}
        </span>
      </div>
      {open ? (
        <div className="tool-body">
          {state?.input ? (
            <>
              <div className="side-label" style={{ padding: 0 }}>
                Entrada
              </div>
              <pre>{JSON.stringify(state.input, null, 2)}</pre>
            </>
          ) : null}
          {state?.output ? (
            <>
              <div className="side-label" style={{ padding: "8px 0 0" }}>
                Saída
              </div>
              <pre>{state.output}</pre>
            </>
          ) : null}
          {state?.error ? (
            <div className="error-banner">{String(state.error)}</div>
          ) : null}
          {(state?.attachments ?? []).map((a, i) => (
            <a key={i} className="chip" href={a.url} target="_blank" rel="noreferrer">
              📎 {a.filename ?? "anexo"}
            </a>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// Card de raciocínio/pensamentos: ABERTO por padrão (tanto durante a resposta
// quanto depois), para os pensamentos aparecerem na hora da resposta. O usuário
// pode recolher clicando no título; o estado manual é respeitado.
function ReasoningPartView({ part, active }: { part: Part; active?: boolean }) {
  const [open, setOpen] = useState(true);
  const thinking = active === true && part.time?.end == null;
  return (
    <details
      className={cx("part", "reasoning-card", thinking && "live")}
      open={open}
      onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}
    >
      <summary>🧠 Pensamentos da IA{thinking ? " · pensando…" : ""}</summary>
      {part.text?.trim() ? (
        <Markdown text={part.text} />
      ) : (
        <div style={{ color: "var(--text-faint)", fontStyle: "italic", paddingBottom: 6 }}>
          gerando pensamentos…
        </div>
      )}
    </details>
  );
}

function PartView({ part, active }: { part: Part; active?: boolean }) {
  switch (part.type) {
    case "text":
      if (!part.text?.trim()) return null;
      return (
        <div className="part">
          <Markdown text={part.text} />
        </div>
      );
    case "reasoning":
      return <ReasoningPartView part={part} active={active} />;
    case "tool":
      return <ToolPartView part={part} />;
    case "file":
      if (part.mime?.startsWith("image/") && part.url) {
        return (
          <div className="part">
            <img src={part.url} alt={part.filename ?? "imagem anexada"} style={{ maxWidth: "100%", borderRadius: 10 }} />
          </div>
        );
      }
      return (
        <div className="part">
          <span className="chip">📎 {part.filename ?? "arquivo"} {part.mime ? `· ${part.mime}` : ""}</span>
        </div>
      );
    case "step-finish":
      return (
        <div className="part" style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <span className="chip">custo {formatCost(part.cost)}</span>
          <span className="chip">{formatTokens(part.tokens)}</span>
          {part.reason ? <span className="chip">{part.reason}</span> : null}
        </div>
      );
    case "step-start":
      return null;
    case "snapshot":
      return (
        <div className="part">
          <span className="chip">snapshot {part.snapshot ?? ""}</span>
        </div>
      );
    case "patch":
      return (
        <div className="part">
          <span className="chip">patch {(part.files ?? []).length} arquivo(s)</span>
        </div>
      );
    case "agent":
      return (
        <div className="part">
          <span className="chip">agente: {part.name ?? part.agent ?? ""}</span>
        </div>
      );
    case "subtask":
      return (
        <div className="part">
          <span className="chip">subtarefa: {part.description ?? part.prompt ?? ""}</span>
        </div>
      );
    case "retry":
      return (
        <div className="part">
          <span className="chip warn">
            nova tentativa #{part.attempt ?? 1}
            {(part.error as { message?: string })?.message
              ? ` — ${(part.error as { message?: string }).message}`
              : ""}
          </span>
        </div>
      );
    case "compaction":
      return (
        <div className="part">
          <span className="chip">contexto compactado{part.auto ? " (automático)" : ""}</span>
        </div>
      );
    default:
      return null;
  }
}

function MessageItem({ msg }: { msg: SessionMessage }) {
  const isUser = msg.info.role === "user";
  const active = !isUser && msg.info.time?.completed == null && !msg.info.error;
  const parts = msg.parts.filter((p) => p.type !== "step-start" || false);
  return (
    <article className="msg">
      <div className={cx("msg-avatar", isUser ? "user" : "assistant")} aria-hidden>
        {isUser ? "VC" : "OC"}
      </div>
      <div className="msg-body">
        <div className="msg-role">
          {isUser ? "Você" : "OpenCode"}
          {!isUser && msg.info.modelID ? <span className="chip">{msg.info.modelID}</span> : null}
          {!isUser && msg.info.agent ? <span className="chip">agente {msg.info.agent}</span> : null}
          {!isUser && msg.info.cost ? <span className="chip">{formatCost(msg.info.cost)}</span> : null}
        </div>
        {msg.info.error ? (
          <div className="error-banner" role="alert">
            Erro: {msg.info.error.message ?? JSON.stringify(msg.info.error)}
          </div>
        ) : null}
        {parts.map((p) => (
          <PartView key={p.id} part={p} active={active} />
        ))}
      </div>
    </article>
  );
}

// ── Composer ───────────────────────────────────────────────────────

function Composer({
  busy,
  model,
  onModelChange,
  providers,
  connected,
  agent,
  onAgentChange,
  agents,
  commands,
  allowAppRun,
  allowAllPermissions,
  permissionsSaving,
  sessionPrefsLoading,
  onAllowAllPermissionsChange,
  onToggleAllowAppRun,
  onSend,
  onAbort,
  onUpload,
  onRunCommand,
}: {
  busy: boolean;
  model: ModelChoice | null;
  onModelChange: (m: ModelChoice | null) => void;
  providers: Provider[];
  connected?: string[];
  agent: string;
  onAgentChange: (a: string) => void;
  agents: AgentInfo[];
  commands: CommandInfo[];
  allowAppRun: boolean;
  allowAllPermissions: boolean;
  permissionsSaving: boolean;
  sessionPrefsLoading: boolean;
  onAllowAllPermissionsChange: (allow: boolean) => void;
  onToggleAllowAppRun: () => void;
  onSend: (text: string, files: Attachment[]) => void | Promise<void>;
  onAbort: () => void;
  onUpload: (file: File) => Promise<Attachment>;
  onRunCommand: (command: string, args: string) => void;
}) {
  const [text, setText] = useState("");
  const [files, setFiles] = useState<Attachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [showCommands, setShowCommands] = useState(false);
  const taRef = useRef<HTMLTextAreaElement>(null);

  // ── Medidor de uso do plano (só quando o modelo é do ChatGPT) ──
  const isChatGpt = model?.providerID === "openai";
  const [usage, setUsage] = useState<ProviderUsageInfo | null>(null);
  const [usageError, setUsageError] = useState<string | null>(null);
  const [usageBusy, setUsageBusy] = useState(false);
  const [reserveNote, setReserveNote] = useState<string | null>(null);

  const modelName = useMemo(() => {
    if (!model) return "";
    const m = providers.find((p) => p.id === model.providerID)?.models?.[model.modelID];
    return m?.name ?? model.modelID;
  }, [model, providers]);

  const openaiModelIDs = useMemo(
    () => Object.keys(providers.find((p) => p.id === "openai")?.models ?? {}),
    [providers],
  );

  const loadUsage = useCallback(
    async (force: boolean) => {
      if (!isChatGpt) return;
      setUsageBusy(true);
      setUsageError(null);
      try {
        const info = await getProviderUsage("openai", force);
        setUsage(info);
        // Ao entrar na reserva (Luna), muda automaticamente para o modelo mais
        // avançado disponível nela — o mesmo comportamento do app do ChatGPT.
        const target = needsReserveSwitch(info, model?.modelID, openaiModelIDs);
        if (target) {
          const name =
            providers.find((p) => p.id === "openai")?.models?.[target]?.name ?? target;
          onModelChange({ providerID: "openai", modelID: target });
          setReserveNote(`Reserva Luna ativa — modelo ajustado para ${name}`);
        } else if (!info.reserve?.active) {
          setReserveNote(null);
        }
      } catch (e) {
        setUsageError((e as Error).message);
      } finally {
        setUsageBusy(false);
      }
    },
    [isChatGpt, model?.modelID, onModelChange, openaiModelIDs, providers],
  );

  useEffect(() => {
    void loadUsage(false);
  }, [loadUsage]);

  useEffect(() => {
    if (!isChatGpt) return;
    const refresh = (event: Event) => {
      const detail = (event as CustomEvent<{ providerID: string }>).detail;
      if (detail?.providerID !== "openai") return;
      setUsage(null);
      void loadUsage(false);
    };
    window.addEventListener(PROVIDER_USAGE_UPDATED, refresh);
    return () => window.removeEventListener(PROVIDER_USAGE_UPDATED, refresh);
  }, [isChatGpt, loadUsage]);

  const commandMatches = useMemo(() => {
    if (!text.startsWith("/")) return [];
    const term = text.slice(1).split(/\s/)[0].toLowerCase();
    return commands.filter((c) => c.name.toLowerCase().startsWith(term)).slice(0, 8);
  }, [text, commands]);

  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, 320)}px`;
  }, [text]);

  async function handleUpload(fileList: FileList | null) {
    if (!fileList?.length) return;
    setUploading(true);
    try {
      const uploaded: Attachment[] = [];
      for (const f of Array.from(fileList)) {
        uploaded.push(await onUpload(f));
      }
      setFiles((prev) => [...prev, ...uploaded]);
    } finally {
      setUploading(false);
    }
  }

  function send() {
    const trimmed = text.trim();
    if (!trimmed && files.length === 0) return;
    if (trimmed.startsWith("/")) {
      const [first, ...rest] = trimmed.slice(1).split(/\s+/);
      const cmd = commands.find((c) => c.name === first);
      if (cmd) {
        onRunCommand(cmd.name, rest.join(" "));
        setText("");
        return;
      }
    }
    void onSend(trimmed, files);
    setText("");
    setFiles([]);
  }

  return (
    <div className="composer-wrap">
      {isChatGpt ? (
        <div className="usage-meter" role="status" aria-label="Uso disponível do plano ChatGPT">
          <span className="usage-meter-model">{modelName}</span>
          {usageError ? (
            <span className="usage-meter-muted">uso indisponível</span>
          ) : usage ? (
            usage.windows.map((w) => {
              const remaining = remainingPercent(w);
              const reset = resetIn(w);
              return (
                <span
                  key={w.id}
                  className={cx("usage-chip", (w.limitReached || remaining <= 20) && "low")}
                  title={reset ? `renovam em ${reset}` : undefined}
                >
                  {shortWindowLabel(w)}: {remaining}%
                </span>
              );
            })
          ) : usageBusy ? (
            <span className="usage-meter-muted">consultando uso…</span>
          ) : null}
          {reserveNote ? (
            <span className="usage-meter-muted" title="As janelas principais estouraram; o plano caiu para a reserva Luna">
              {reserveNote}
            </span>
          ) : null}
          <button
            className="icon-btn"
            title="Atualizar uso do plano"
            aria-label="Atualizar uso do plano"
            disabled={usageBusy}
            onClick={() => void loadUsage(true)}
          >
            ↻
          </button>
        </div>
      ) : null}
      <div className="composer">
        <label className="sr-only" htmlFor="composer-input" style={{ position: "absolute", left: -9999 }}>
          Mensagem para o OpenCode
        </label>
        <textarea
          id="composer-input"
          ref={taRef}
          value={text}
          placeholder="Descreva a tarefa…  (Ctrl+Enter envia, / aciona comandos)"
          onChange={(e) => {
            setText(e.target.value);
            setShowCommands(e.target.value.startsWith("/"));
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              send();
            }
            if (e.key === "Escape") setShowCommands(false);
          }}
          aria-describedby="composer-hint"
        />

        {showCommands && commandMatches.length > 0 ? (
          <div
            style={{
              border: "1px solid var(--border)",
              borderRadius: 10,
              marginBottom: 8,
              overflow: "hidden",
            }}
            role="listbox"
            aria-label="Comandos disponíveis"
          >
            {commandMatches.map((c) => (
              <button
                key={c.name}
                className="session-item"
                role="option"
                onClick={() => {
                  setText(`/${c.name} `);
                  setShowCommands(false);
                  taRef.current?.focus();
                }}
              >
                <span className="tool-name">/{c.name}</span>
                <span className="title" style={{ color: "var(--text-muted)" }}>
                  {c.description ?? c.template}
                </span>
              </button>
            ))}
          </div>
        ) : null}

        {files.length > 0 ? (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", paddingBottom: 8 }}>
            {files.map((f) => (
              <span key={f.uri} className="attach-chip">
                📎 {f.name}
                <button
                  className="icon-btn"
                  aria-label={`Remover anexo ${f.name}`}
                  onClick={() => setFiles((prev) => prev.filter((x) => x.uri !== f.uri))}
                >
                  ✕
                </button>
              </span>
            ))}
          </div>
        ) : null}

        <div className="composer-row">
          <label className="btn ghost sm" style={{ cursor: "pointer" }}>
            {uploading ? "Enviando…" : "+ Anexar"}
            <input
              type="file"
              multiple
              style={{ display: "none" }}
              onChange={(e) => void handleUpload(e.target.files)}
              disabled={uploading}
            />
          </label>

          <ModelSelect
            providers={providers}
            value={model}
            onChange={onModelChange}
            connected={connected}
            ariaLabel="Modelo"
          />

          <select
            className="select"
            aria-label="Agente"
            value={agent}
            onChange={(e) => onAgentChange(e.target.value)}
          >
            <option value="">Agente padrão</option>
            {agents.map((a) => (
              <option key={a.name} value={a.name}>
                {a.name}
              </option>
            ))}
          </select>

          <button
            className={cx("btn sm", allowAllPermissions || allowAppRun ? "ghost" : "danger")}
            aria-pressed={allowAllPermissions || allowAppRun}
            title={
              allowAllPermissions
                ? "Permitir tudo nesta sessão também permite iniciar a aplicação."
                : allowAppRun
                ? "A aplicação pode subir nesta sessão (ex.: npm run dev). Clique para bloquear."
                : "A aplicação não sobe nesta sessão; comandos comuns (instalar, compilar, testar) executam normalmente. Clique para permitir subir a aplicação."
            }
            onClick={onToggleAllowAppRun}
            disabled={allowAllPermissions || permissionsSaving || sessionPrefsLoading}
          >
            {allowAllPermissions ? "▶ Aplicação: permitir tudo" : allowAppRun ? "▶ Aplicação: pode rodar" : "■ Aplicação: bloqueada"}
          </button>

          <span className="spacer" />

          {busy ? (
            <button className="btn danger" onClick={onAbort}>
              ■ Interromper
            </button>
          ) : (
            <button className="btn primary" onClick={send} disabled={uploading}>
              Enviar ▸
            </button>
          )}
        </div>
        <div style={{ paddingTop: 8 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={allowAllPermissions}
              disabled={permissionsSaving || sessionPrefsLoading}
              onChange={(e) => onAllowAllPermissionsChange(e.target.checked)}
              aria-label="Permitir tudo nesta sessão"
              aria-describedby="composer-allow-all-description"
            />
            <strong>Permitir tudo nesta sessão</strong>
            {permissionsSaving || sessionPrefsLoading ? (
              <span role="status" style={{ color: "var(--text-muted)" }}>
                {sessionPrefsLoading ? "Carregando…" : "Salvando…"}
              </span>
            ) : null}
          </label>
          <div id="composer-allow-all-description" style={{ fontSize: 11.5, color: "var(--text-muted)", paddingTop: 4 }}>
            Comandos e alterações em arquivos ficam sem confirmação, incluindo iniciar a aplicação.
          </div>
        </div>
        <div id="composer-hint" style={{ fontSize: 11, color: "var(--text-faint)", paddingTop: 4 }}>
          Ctrl+Enter envia · Markdown suportado · anexos vão para o workspace autorizado
        </div>
      </div>
    </div>
  );
}

// ── Chat completo ──────────────────────────────────────────────────

export function ChatView(props: ChatViewProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  const lastCount = useRef(0);
  const lastStatus = statusLabel(props.status);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    const count = props.messages.length;
    const streamingChanged = count === lastCount.current;
    lastCount.current = count;
    if (!el) return;
    // Rolar apenas o contêiner de mensagens (não a página) e somente quando
    // o usuário está colado no fim — durante streaming, rolagem manual é preservada.
    if (stick.current || !streamingChanged) {
      if (streamingChanged) {
        el.scrollTop = el.scrollHeight;
      } else {
        el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
      }
    }
  }, [props.messages]);

  return (
    <section className="main-col" aria-label="Conversa">
      <header className="chat-header">
        <button
          className="icon-btn mobile-only"
          aria-label="Abrir menu lateral"
          onClick={() => document.dispatchEvent(new CustomEvent("oc:open-sidebar"))}
        >
          ☰
        </button>
        <h1 title={props.session?.title ?? ""}>
          {props.session?.title || props.session?.slug || "Selecione ou crie uma sessão"}
        </h1>

        {props.session ? (
          <>
            <button className="btn ghost sm" onClick={() => props.onFork()} title="Bifurcar esta sessão">
              Bifurcar
            </button>
            <button className="btn ghost sm" onClick={props.onSummarize} title="Resumir a sessão">
              Resumir
            </button>
            <button
              className="btn ghost sm"
              onClick={props.onShareToggle}
              title={props.session.share?.url ? "Deixar de compartilhar" : "Compartilhar sessão"}
            >
              {props.session.share?.url ? "Compartilhada" : "Compartilhar"}
            </button>
            <button className="btn ghost sm" onClick={props.onInit} title="Gerar AGENTS.md do projeto">
              Inicializar
            </button>
            <button
              className="btn ghost sm"
              title="Reverter a última mensagem do assistente"
              onClick={() => {
                const lastAssistant = [...props.messages].reverse().find((m) => m.info.role === "assistant");
                if (lastAssistant) props.onRevert(lastAssistant.info.id);
              }}
            >
              ↩ Reverter última
            </button>
            <button className="btn ghost sm" onClick={props.onUnrevert} title="Restaurar mensagens revertidas">
              Restaurar
            </button>
            <button className="btn ghost sm" onClick={props.onOpenSettings} title="Configurações">
              ⚙
            </button>
          </>
        ) : null}
      </header>

      <div className="messages" ref={scrollRef} aria-live="polite">
        {props.session?.share?.url ? (
          <div className="notice-banner">
            Sessão compartilhada:{" "}
            <a href={props.session.share.url} target="_blank" rel="noreferrer">
              {props.session.share.url}
            </a>
          </div>
        ) : null}

        {lastStatus ? (
          <div className="notice-banner" role="status">
            {lastStatus}
          </div>
        ) : null}

        {props.messages.length === 0 ? (
          <div style={{ maxWidth: 640, margin: "60px auto", textAlign: "center", color: "var(--text-muted)" }}>
            <h2 style={{ color: "var(--text)" }}>Comece a programar com o OpenCode</h2>
            <p>
              Envie uma tarefa em linguagem natural. O motor OpenCode real executa no workspace autorizado,
              com aprovação de permissões, diffs e histórico preservados.
            </p>
            <p style={{ fontSize: 13 }}>
              Dica: use <strong>/</strong> para comandos personalizados e <strong>Ctrl+K</strong> para pesquisar sessões.
            </p>
          </div>
        ) : (
          props.messages.map((m) => <MessageItem key={m.info.id} msg={m} />)
        )}
        <div ref={bottomRef} />
      </div>

      {props.session ? (
        <Composer
          busy={props.busy}
          model={props.model}
          onModelChange={props.onModelChange}
          providers={props.providers}
          connected={props.connected}
          agent={props.agent}
          onAgentChange={props.onAgentChange}
          agents={props.agents}
          commands={props.commands}
          allowAppRun={props.allowAppRun}
          allowAllPermissions={props.allowAllPermissions}
          permissionsSaving={props.permissionsSaving}
          sessionPrefsLoading={props.sessionPrefsLoading}
          onAllowAllPermissionsChange={props.onAllowAllPermissionsChange}
          onToggleAllowAppRun={props.onToggleAllowAppRun}
          onSend={props.onSend}
          onAbort={props.onAbort}
          onUpload={props.onUpload}
          onRunCommand={props.onRunCommand}
        />
      ) : (
        <div className="composer-wrap">
          <div className="composer" style={{ textAlign: "center", color: "var(--text-muted)" }}>
            Crie uma sessão na barra lateral para começar.
          </div>
        </div>
      )}
    </section>
  );
}

export function fileLanguage(name: string): string {
  return languageFromFilename(name);
}
